/**
 * device/apps/scribe.ts — The Scribe / Notes App
 *
 * A warm leather-bound notebook for the in-game device. Two views:
 * a scrollable list of note cards and a detail view with simple
 * markdown rendering. Every stroke is Canvas 2D — no DOM, no HTML,
 * just parchment, amber, and a quill.
 *
 * Colors are warm-dark: deep blacks with amber gold accents, cream text,
 * and category-colored dots. It should feel like opening your journal
 * beside a hearthfire.
 */

import type { GameState } from "../../engine/state.js";
import { type DeviceApp, type Rect, drawRoundRect, drawText, registerApp } from "../app.js";

// ─── App ID ───────────────────────────────────────────────────────────────────

const APP_ID   = "scribe";
const APP_NAME = "Scribe";

// ─── Color Palette ────────────────────────────────────────────────────────────

const C_BG            = "#0e0c0a";   // deep warm black
const C_CARD_BG       = "#2a2018";   // warm dark brown
const C_CARD_BORDER   = "rgba(200,168,120,0.12)";
const C_TITLE         = "#f5e6d0";   // warm cream
const C_BODY          = "#c8b898";   // warm tan
const C_DIM           = "#8a7860";   // muted warm
const C_ACCENT        = "#e8a050";   // amber gold
const C_SEARCH_BG     = "#1a1410";   // dark warm
const C_SEPARATOR     = "rgba(200,168,120,0.08)";
const C_PIN           = "#f0c860";   // gold
const C_NEW_BTN_START = "#e8a050";
const C_NEW_BTN_END   = "#c88030";

// Category colors
const C_CAT: Record<string, string> = {
  general:  "#8a8070",
  quest:    "#c8a050",
  recipe:   "#6ab060",
  personal: "#6080b0",
  shared:   "#a070b0",
};

// ─── Data Types ───────────────────────────────────────────────────────────────

type NoteCategory = "general" | "quest" | "recipe" | "personal" | "shared";

interface ScribeNote {
  id: string;
  title: string;
  body: string;
  category: NoteCategory;
  createdAt: number;
  updatedAt: number;
  pinned: boolean;
  sharedWith: string[];
}

interface ScribeState {
  notes: ScribeNote[];
  activeNoteId: string | null;
  scrollY: number;
  scrollVelocity: number;
  maxScrollY: number;
  searchQuery: string;
  editCursorLine: number;
  activeCategory: string;   // "all" | NoteCategory
  /** Which field is being edited: "title", "body", "search", or null */
  editingField: "title" | "body" | "search" | null;
}

// ─── Layout Constants ─────────────────────────────────────────────────────────

const PAD          = 8;
const CARD_GAP     = 5;
const CARD_RADIUS  = 6;
const CARD_H       = 54;
const SEARCH_H     = 24;
const FILTER_H     = 22;
const FILTER_GAP   = 4;
const NEW_BTN_H    = 28;
const NEW_BTN_R    = 8;
const CAT_DOT_R    = 3;
const HEADER_H     = SEARCH_H + FILTER_H + PAD * 2 + FILTER_GAP;
const BOTTOM_PAD   = NEW_BTN_H + PAD * 2;

const FONT_TITLE   = "bold 10px sans-serif";
const FONT_BODY    = "8px sans-serif";
const FONT_SMALL   = "7px sans-serif";
const FONT_CAT     = "bold 7px sans-serif";
const FONT_DETAIL_TITLE = "bold 12px Georgia, serif";
const FONT_H1      = "bold 11px Georgia, serif";
const FONT_H2      = "bold 10px Georgia, serif";
const FONT_DETAIL  = "9px sans-serif";
const FONT_DETAIL_ITALIC = "italic 9px sans-serif";
const FONT_DETAIL_BOLD   = "bold 9px sans-serif";

// ─── Starter Notes ────────────────────────────────────────────────────────────

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

const STARTER_NOTES: ScribeNote[] = [
  {
    id: makeId(),
    title: "Welcome to Hearth",
    body: "# Your Scribe\n\nThis is your notebook. Write anything — quest clues, recipes, thoughts, memories.\n\n*Your Aeterna can read your notes.*",
    category: "general",
    createdAt: Date.now() - 1000 * 60 * 60 * 24 * 3,
    updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 3,
    pinned: false,
    sharedWith: [],
  },
  {
    id: makeId(),
    title: "Grandma's Stew",
    body: "# Hearth Stew\n\n- 3 root vegetables\n- 1 wild herb\n- Spring water\n- A pinch of patience\n\n**Simmer until the room smells like home.**",
    category: "recipe",
    createdAt: Date.now() - 1000 * 60 * 60 * 24 * 7,
    updatedAt: Date.now() - 1000 * 60 * 60 * 24 * 7,
    pinned: false,
    sharedWith: [],
  },
  {
    id: makeId(),
    title: "Strange Sound",
    body: "Heard something in the walls near the bookshelf. The Aeterna said it noticed it too.\n\n*Come back at midnight.*",
    category: "quest",
    createdAt: Date.now() - 1000 * 60 * 60 * 2,
    updatedAt: Date.now() - 1000 * 60 * 60 * 2,
    pinned: true,
    sharedWith: [],
  },
] satisfies ScribeNote[];

// ─── State Accessor ───────────────────────────────────────────────────────────

function getState(state: GameState): ScribeState {
  if (!state.player.device.appStates[APP_ID]) {
    state.player.device.appStates[APP_ID] = {
      notes: STARTER_NOTES.map(n => ({ ...n })),
      activeNoteId: null,
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
      searchQuery: "",
      editCursorLine: 0,
      activeCategory: "all",
      editingField: null,
    } satisfies ScribeState;
  }
  return state.player.device.appStates[APP_ID] as ScribeState;
}

// ─── Time Formatting ──────────────────────────────────────────────────────────

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins < 1)    return "now";
  if (mins < 60)   return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days < 30)   return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// ─── Note Filtering ───────────────────────────────────────────────────────────

function filterNotes(s: ScribeState): ScribeNote[] {
  let notes = s.notes.slice();

  // Category filter
  if (s.activeCategory !== "all") {
    notes = notes.filter(n => n.category === s.activeCategory);
  }

  // Search filter
  const q = s.searchQuery.trim().toLowerCase();
  if (q.length > 0) {
    notes = notes.filter(
      n => n.title.toLowerCase().includes(q) || n.body.toLowerCase().includes(q),
    );
  }

  // Pinned notes float to top
  notes.sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return b.updatedAt - a.updatedAt;
  });

  return notes;
}

// ─── Markdown Line Renderer ───────────────────────────────────────────────────

/**
 * Render a single line of markdown-ish text onto the canvas.
 * Handles: # H1, ## H2, - list, **bold**, *italic*, plain text.
 * Returns the height consumed by this line.
 */
function renderMarkdownLine(
  ctx: CanvasRenderingContext2D,
  line: string,
  x: number,
  y: number,
  maxW: number,
): number {
  // H1
  if (line.startsWith("# ")) {
    const text = line.slice(2).trim();
    ctx.font = FONT_H1;
    ctx.fillStyle = C_ACCENT;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    drawText(ctx, text, x, y, maxW, FONT_H1, C_ACCENT);
    return 16;
  }

  // H2
  if (line.startsWith("## ")) {
    const text = line.slice(3).trim();
    drawText(ctx, text, x, y, maxW, FONT_H2, C_TITLE);
    return 14;
  }

  // List item
  if (line.startsWith("- ")) {
    const text = line.slice(2).trim();
    // Draw warm bullet dot
    ctx.beginPath();
    ctx.arc(x + 3, y + 5, 2, 0, Math.PI * 2);
    ctx.fillStyle = C_ACCENT;
    ctx.fill();
    // Draw text after bullet
    renderInlineMarkdown(ctx, text, x + 10, y, maxW - 10);
    return 13;
  }

  // Empty line
  if (line.trim() === "") {
    return 6;
  }

  // Regular text with possible inline formatting
  renderInlineMarkdown(ctx, line, x, y, maxW);
  return 13;
}

/**
 * Render a line of text with inline **bold** and *italic* spans.
 * Does not handle nested spans — keeps it simple and fast.
 */
function renderInlineMarkdown(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
): void {
  // Check for bold (**...**) or italic (*...*)
  // We walk through the string segment by segment.
  const segments = parseInlineSegments(text);

  let cursorX = x;
  const remainingW = maxW;

  for (const seg of segments) {
    if (cursorX - x >= remainingW) break;

    let font: string;
    let color: string;

    if (seg.bold) {
      font = FONT_DETAIL_BOLD;
      color = C_TITLE;
    } else if (seg.italic) {
      font = FONT_DETAIL_ITALIC;
      color = C_BODY;
    } else {
      font = FONT_DETAIL;
      color = C_BODY;
    }

    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";

    const availW = maxW - (cursorX - x);
    if (availW <= 0) break;

    let display = seg.text;
    while (ctx.measureText(display).width > availW && display.length > 1) {
      display = display.slice(0, -2) + "\u2026";
    }
    ctx.fillText(display, cursorX, y);
    cursorX += ctx.measureText(display).width;
  }
}

interface TextSegment {
  text: string;
  bold: boolean;
  italic: boolean;
}

/**
 * Parse a string into plain, bold, and italic segments.
 * Bold (**text**) takes precedence over italic (*text*).
 */
function parseInlineSegments(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    // Try bold first (**...**)
    const boldMatch = remaining.indexOf("**");
    const italicMatch = remaining.indexOf("*");

    // Determine which comes first (and is a valid pair)
    const boldStart = boldMatch >= 0 ? boldMatch : Infinity;
    const italicStart = italicMatch >= 0 ? italicMatch : Infinity;

    if (boldStart === Infinity && italicStart === Infinity) {
      // No formatting at all
      segments.push({ text: remaining, bold: false, italic: false });
      break;
    }

    if (boldStart <= italicStart && boldStart !== Infinity) {
      // Bold segment
      const closeIdx = remaining.indexOf("**", boldStart + 2);
      if (closeIdx < 0) {
        // No closing ** — treat rest as plain
        segments.push({ text: remaining, bold: false, italic: false });
        break;
      }
      // Plain text before bold
      if (boldStart > 0) {
        segments.push({ text: remaining.slice(0, boldStart), bold: false, italic: false });
      }
      segments.push({ text: remaining.slice(boldStart + 2, closeIdx), bold: true, italic: false });
      remaining = remaining.slice(closeIdx + 2);
    } else {
      // Italic segment — make sure it's not ** (already handled above)
      const singleStar = italicStart;
      if (remaining[singleStar + 1] === "*") {
        // This is a **, skip one char and retry
        segments.push({ text: remaining.slice(0, singleStar + 1), bold: false, italic: false });
        remaining = remaining.slice(singleStar + 1);
        continue;
      }
      const closeIdx = remaining.indexOf("*", singleStar + 1);
      if (closeIdx < 0) {
        segments.push({ text: remaining, bold: false, italic: false });
        break;
      }
      if (singleStar > 0) {
        segments.push({ text: remaining.slice(0, singleStar), bold: false, italic: false });
      }
      segments.push({ text: remaining.slice(singleStar + 1, closeIdx), bold: false, italic: true });
      remaining = remaining.slice(closeIdx + 1);
    }
  }

  return segments;
}

// ─── Note Body Renderer ───────────────────────────────────────────────────────

/**
 * Render a multi-line note body (markdown-ish).
 * Returns the total height consumed.
 */
function renderNoteBody(
  ctx: CanvasRenderingContext2D,
  body: string,
  x: number,
  y: number,
  maxW: number,
): number {
  const lines = body.split("\n");
  let cursorY = y;
  for (const line of lines) {
    const lineH = renderMarkdownLine(ctx, line, x, cursorY, maxW);
    cursorY += lineH;
  }
  return cursorY - y;
}

/**
 * Measure the total height of a rendered note body without drawing.
 */
function measureNoteBodyHeight(body: string): number {
  // Rough approximation without ctx (can't measure inline segments without canvas)
  const lines = body.split("\n");
  let total = 0;
  for (const line of lines) {
    if (line.startsWith("# "))    total += 16;
    else if (line.startsWith("## ")) total += 14;
    else if (line.startsWith("- ")) total += 13;
    else if (line.trim() === "")    total += 6;
    else                            total += 13;
  }
  return total;
}

// ─── Search Bar ───────────────────────────────────────────────────────────────

function drawSearchBar(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
  query: string,
): void {
  const h = SEARCH_H;
  drawRoundRect(ctx, x, y, w, h, h / 2, C_SEARCH_BG, "rgba(200,168,120,0.15)");

  // Magnifying glass icon
  const iconX = x + 10;
  const iconCY = y + h / 2;
  ctx.save();
  ctx.strokeStyle = C_DIM;
  ctx.lineWidth = 1.2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(iconX, iconCY, 4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(iconX + 3, iconCY + 3);
  ctx.lineTo(iconX + 6, iconCY + 6);
  ctx.stroke();
  ctx.restore();

  // Query or placeholder
  const textX = x + 22;
  const textW = w - 26;
  if (query.length > 0) {
    drawText(ctx, query, textX, y + (h - 8) / 2, textW, "9px sans-serif", C_BODY);
  } else {
    drawText(ctx, "Search notes…", textX, y + (h - 8) / 2, textW, "9px sans-serif", C_DIM);
  }
}

// ─── Category Filter Row ─────────────────────────────────────────────────────

const CATEGORIES: Array<{ id: string; label: string }> = [
  { id: "all",      label: "All"      },
  { id: "quest",    label: "Quest"    },
  { id: "recipe",   label: "Recipe"   },
  { id: "personal", label: "Personal" },
  { id: "shared",   label: "Shared"   },
];

/**
 * Draw filter pills and return an array of hit rects for tap handling.
 */
function drawFilterRow(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
  activeCategory: string,
): Array<{ id: string; x: number; w: number }> {
  const hitRects: Array<{ id: string; x: number; w: number }> = [];
  ctx.font = FONT_CAT;
  ctx.textBaseline = "top";

  let cursorX = x;
  const pillH = FILTER_H - 4;
  const pillY = y + 2;

  for (const cat of CATEGORIES) {
    const labelW = ctx.measureText(cat.label).width + 14;
    const isActive = cat.id === activeCategory;

    const fill  = isActive ? C_ACCENT           : "rgba(200,168,120,0.08)";
    const stroke = isActive ? C_ACCENT           : "rgba(200,168,120,0.18)";
    const tcolor = isActive ? "#0e0c0a"          : C_DIM;

    drawRoundRect(ctx, cursorX, pillY, labelW, pillH, pillH / 2, fill, stroke);
    drawText(ctx, cat.label, cursorX + 7, pillY + 3, labelW - 8, FONT_CAT, tcolor);

    hitRects.push({ id: cat.id, x: cursorX, w: labelW });
    cursorX += labelW + FILTER_GAP;
    if (cursorX >= x + w) break; // overflow safety
  }

  return hitRects;
}

// ─── Note Card ────────────────────────────────────────────────────────────────

function drawNoteCard(
  ctx: CanvasRenderingContext2D,
  note: ScribeNote,
  x: number, y: number, w: number,
): void {
  const h = CARD_H;

  // Card background
  drawRoundRect(ctx, x, y, w, h, CARD_RADIUS, C_CARD_BG, C_CARD_BORDER);

  // Category dot — left edge
  const dotX = x + 7;
  const dotY = y + h / 2;
  const dotColor = C_CAT[note.category] ?? C_CAT.general;
  ctx.beginPath();
  ctx.arc(dotX, dotY, CAT_DOT_R, 0, Math.PI * 2);
  ctx.fillStyle = dotColor;
  ctx.fill();

  // Content area
  const contentX = x + 16;
  const contentW = w - 22;

  // Pin icon (top-right) if pinned
  const pinX = x + w - 10;
  if (note.pinned) {
    drawPinIcon(ctx, pinX, y + 8, 6);
  }

  // Title
  const titleMaxW = contentW - (note.pinned ? 14 : 4);
  drawText(ctx, note.title, contentX, y + 8, titleMaxW, FONT_TITLE, C_TITLE);

  // Body preview — first non-empty line, strip markdown symbols
  const preview = getBodyPreview(note.body);
  drawText(ctx, preview, contentX, y + 22, contentW - 4, FONT_BODY, C_DIM);

  // Timestamp — bottom right
  const ts = relativeTime(note.updatedAt);
  drawText(ctx, ts, x + w - PAD, y + h - 11, 52, FONT_SMALL, C_DIM, "right");

  // Category label — bottom left, small
  const catColor = C_CAT[note.category] ?? C_CAT.general;
  drawText(ctx, note.category, contentX, y + h - 11, 44, FONT_SMALL, catColor);
}

function getBodyPreview(body: string): string {
  const lines = body.split("\n");
  for (const line of lines) {
    const clean = line
      .replace(/^#+\s*/, "")
      .replace(/^-\s+/, "")
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1")
      .trim();
    if (clean.length > 0) return clean;
  }
  return "";
}

// ─── Pin Icon ─────────────────────────────────────────────────────────────────

function drawPinIcon(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
): void {
  ctx.save();
  ctx.fillStyle = C_PIN;
  ctx.strokeStyle = C_PIN;
  ctx.lineWidth = 0.8;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Star — 5 points
  const outerR = size;
  const innerR = size * 0.42;
  const points = 5;

  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const angle = (Math.PI / points) * i - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    const px = cx + Math.cos(angle) * r;
    const py = cy + Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ─── "New Note" Button ────────────────────────────────────────────────────────

function drawNewButton(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
): void {
  const btnW = w - PAD * 2;
  const btnX = x + PAD;
  const btnY = y;

  const grad = ctx.createLinearGradient(btnX, btnY, btnX + btnW, btnY);
  grad.addColorStop(0, C_NEW_BTN_START);
  grad.addColorStop(1, C_NEW_BTN_END);

  drawRoundRect(ctx, btnX, btnY, btnW, NEW_BTN_H, NEW_BTN_R, undefined, undefined);
  // Fill manually with gradient
  ctx.beginPath();
  ctx.roundRect?.(btnX, btnY, btnW, NEW_BTN_H, NEW_BTN_R);
  ctx.fillStyle = grad;
  ctx.fill();

  // + icon
  const icX = btnX + btnW / 2 - 28;
  const icY = btnY + NEW_BTN_H / 2;
  ctx.strokeStyle = "#0e0c0a";
  ctx.lineWidth = 1.8;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(icX - 5, icY);
  ctx.lineTo(icX + 5, icY);
  ctx.moveTo(icX, icY - 5);
  ctx.lineTo(icX, icY + 5);
  ctx.stroke();

  drawText(ctx, "New Note", btnX + btnW / 2 - 10, btnY + 7, btnW - 16, FONT_TITLE, "#0e0c0a");
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function drawDetailView(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  s: ScribeState,
  dt: number,
): void {
  const note = s.notes.find(n => n.id === s.activeNoteId);
  if (!note) return;

  const { x, y, w, h } = rect;

  // ── Scroll momentum ──────────────────────────────────────────────────────
  if (Math.abs(s.scrollVelocity) > 0.5) {
    s.scrollY += s.scrollVelocity * dt;
    s.scrollVelocity *= Math.exp(-6 * dt);
  } else {
    s.scrollVelocity = 0;
  }

  // ── Header ───────────────────────────────────────────────────────────────
  const headerH = 52;

  // Header background
  ctx.fillStyle = C_BG;
  ctx.fillRect(x, y, w, headerH);

  // Category dots row — tap to change category
  let dotCursorX = x + PAD;
  const catDotY = y + 6;
  for (const catKey of Object.keys(C_CAT) as NoteCategory[]) {
    const isActive = note.category === catKey;
    const dc = C_CAT[catKey];
    ctx.beginPath();
    ctx.arc(dotCursorX + 5, catDotY + 5, isActive ? 5 : 3.5, 0, Math.PI * 2);
    ctx.fillStyle = dc;
    ctx.globalAlpha = isActive ? 1.0 : 0.4;
    ctx.fill();
    ctx.globalAlpha = 1.0;
    if (isActive) {
      // Small label below active dot
      drawText(ctx, catKey, dotCursorX - 6, catDotY + 12, 50, FONT_SMALL, dc);
    }
    dotCursorX += 20;
  }

  // Pin toggle icon (top right of header)
  drawPinIcon(ctx, x + w - PAD - 6, y + 10, note.pinned ? 7 : 5);
  if (!note.pinned) {
    ctx.globalAlpha = 0.3;
    drawPinIcon(ctx, x + w - PAD - 6, y + 10, 5);
    ctx.globalAlpha = 1.0;
  }

  // Title (tappable to edit)
  drawText(ctx, note.title, x + PAD, y + 26, w - PAD * 2 - 30, FONT_DETAIL_TITLE, C_TITLE);

  // Edit pencil icon next to title
  const pencilX = x + w - PAD - 14;
  const pencilY = y + 30;
  ctx.save();
  ctx.strokeStyle = s.editingField === "title" ? C_ACCENT : C_DIM;
  ctx.lineWidth = 1.2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(pencilX, pencilY + 6);
  ctx.lineTo(pencilX + 6, pencilY);
  ctx.lineTo(pencilX + 8, pencilY + 2);
  ctx.lineTo(pencilX + 2, pencilY + 8);
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(pencilX, pencilY + 8);
  ctx.lineTo(pencilX + 2, pencilY + 8);
  ctx.stroke();
  ctx.restore();

  // Underline under title when editing
  if (s.editingField === "title") {
    ctx.fillStyle = C_ACCENT;
    ctx.fillRect(x + PAD, y + 40, w - PAD * 2 - 22, 1);
  }

  // Separator
  ctx.fillStyle = C_SEPARATOR;
  ctx.fillRect(x, y + headerH - 1, w, 1);

  // ── Scrollable body ───────────────────────────────────────────────────────
  const bodyArea = { x, y: y + headerH, w, h: h - headerH - NEW_BTN_H - PAD };

  // Measure body height to compute maxScrollY
  const measuredBodyH = measureNoteBodyHeight(note.body);
  const padding = 14;
  s.maxScrollY = Math.max(0, measuredBodyH + padding * 2 - bodyArea.h);
  s.scrollY = Math.max(0, Math.min(s.scrollY, s.maxScrollY));

  ctx.save();
  ctx.beginPath();
  ctx.rect(bodyArea.x, bodyArea.y, bodyArea.w, bodyArea.h);
  ctx.clip();

  const bodyX = bodyArea.x + PAD;
  const bodyW = bodyArea.w - PAD * 2;
  const startY = bodyArea.y + padding - s.scrollY;

  if (note.body.length === 0) {
    // Empty note — show tap-to-edit prompt
    ctx.font = "italic 9px sans-serif";
    ctx.fillStyle = C_DIM;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("Tap here to start writing...", bodyX, startY);
  } else {
    renderNoteBody(ctx, note.body, bodyX, startY, bodyW);
  }

  // Subtle editing highlight
  if (s.editingField === "body") {
    ctx.fillStyle = "rgba(232,160,80,0.05)";
    ctx.fillRect(bodyArea.x, bodyArea.y, bodyArea.w, bodyArea.h);
    // Blinking cursor at bottom of content
    const cursorBlink = Math.sin(Date.now() * 0.006) > 0;
    if (cursorBlink) {
      ctx.fillStyle = C_ACCENT;
      ctx.fillRect(bodyX, startY + (note.body.length === 0 ? 14 : measureNoteBodyHeight(note.body) + 4), 1, 10);
    }
  }

  // Top fade
  const fadeH = 10;
  if (s.scrollY > 0) {
    const fadeGrad = ctx.createLinearGradient(x, bodyArea.y, x, bodyArea.y + fadeH);
    fadeGrad.addColorStop(0, C_BG);
    fadeGrad.addColorStop(1, "rgba(14,12,10,0)");
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(x, bodyArea.y, w, fadeH);
  }

  // Bottom fade
  if (s.scrollY < s.maxScrollY) {
    const btmFade = ctx.createLinearGradient(x, bodyArea.y + bodyArea.h - fadeH, x, bodyArea.y + bodyArea.h);
    btmFade.addColorStop(0, "rgba(14,12,10,0)");
    btmFade.addColorStop(1, C_BG);
    ctx.fillStyle = btmFade;
    ctx.fillRect(x, bodyArea.y + bodyArea.h - fadeH, w, fadeH);
  }

  ctx.restore();

  // ── Share button ──────────────────────────────────────────────────────────
  const shareBtnY = y + h - NEW_BTN_H - PAD;
  const shareBtnX = x + PAD;
  const shareBtnW = w - PAD * 2;

  drawRoundRect(ctx, shareBtnX, shareBtnY, shareBtnW, NEW_BTN_H, NEW_BTN_R,
    "rgba(160,112,176,0.18)", "rgba(160,112,176,0.35)");

  // Share icon (box with upward arrow)
  const shareIcX = shareBtnX + shareBtnW / 2 - 30;
  const shareIcY = shareBtnY + NEW_BTN_H / 2;
  ctx.strokeStyle = "#a070b0";
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(shareIcX, shareIcY + 4);
  ctx.lineTo(shareIcX, shareIcY - 4);
  ctx.lineTo(shareIcX - 4, shareIcY);
  ctx.moveTo(shareIcX, shareIcY - 4);
  ctx.lineTo(shareIcX + 4, shareIcY);
  ctx.stroke();

  drawText(ctx, "Share Note", shareBtnX + shareBtnW / 2 - 8, shareBtnY + 7,
    shareBtnW - 16, FONT_TITLE, "#a070b0");
}

// ─── List View ────────────────────────────────────────────────────────────────

/**
 * Cached filter-row hit rects (set each frame, read by handleTap).
 * Stored in module scope so handleTap can access them without re-rendering.
 */
let _filterHitRects: Array<{ id: string; x: number; w: number }> = [];
let _filterRowY = 0;
let _cardHitRects: Array<{ id: string; y: number }> = [];
let _newBtnY = 0;
let _listContentX = 0;
let _listContentY = 0;

function drawListView(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  s: ScribeState,
  dt: number,
): void {
  const { x, y, w, h } = rect;

  // ── Scroll momentum ──────────────────────────────────────────────────────
  if (Math.abs(s.scrollVelocity) > 0.5) {
    s.scrollY += s.scrollVelocity * dt;
    s.scrollVelocity *= Math.exp(-6 * dt);
  } else {
    s.scrollVelocity = 0;
  }

  // ── Fixed header: search + filter ─────────────────────────────────────
  const searchY  = y + PAD;
  const filterY  = searchY + SEARCH_H + FILTER_GAP;
  const contentStartY = filterY + FILTER_H + PAD;

  _listContentX = x;
  _listContentY = contentStartY;

  // Background fill behind header
  ctx.fillStyle = C_BG;
  ctx.fillRect(x, y, w, contentStartY - y);

  drawSearchBar(ctx, x + PAD, searchY, w - PAD * 2, s.searchQuery);
  _filterRowY = filterY;
  _filterHitRects = drawFilterRow(ctx, x + PAD, filterY, w - PAD * 2, s.activeCategory);

  // Separator
  ctx.fillStyle = C_SEPARATOR;
  ctx.fillRect(x, contentStartY - 1, w, 1);

  // ── Scrollable list ───────────────────────────────────────────────────
  const listH    = h - (contentStartY - y) - BOTTOM_PAD;
  const filtered = filterNotes(s);

  // Total content height
  const totalContentH = filtered.length * (CARD_H + CARD_GAP) + PAD;
  s.maxScrollY = Math.max(0, totalContentH - listH);
  s.scrollY = Math.max(0, Math.min(s.scrollY, s.maxScrollY));

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, contentStartY, w, listH);
  ctx.clip();

  let cardY = contentStartY + PAD - s.scrollY;
  _cardHitRects = [];

  const cardX = x + PAD;
  const cardW = w - PAD * 2;

  for (const note of filtered) {
    const bottom = cardY + CARD_H;
    if (bottom < contentStartY) {
      // Fully above viewport — store hit rect so tap still tracks it later
      // (won't be visible, but we still need it in the list)
      _cardHitRects.push({ id: note.id, y: cardY });
      cardY += CARD_H + CARD_GAP;
      continue;
    }
    if (cardY > contentStartY + listH) break;

    drawNoteCard(ctx, note, cardX, cardY, cardW);
    _cardHitRects.push({ id: note.id, y: cardY });
    cardY += CARD_H + CARD_GAP;
  }

  // Empty state
  if (filtered.length === 0) {
    ctx.globalAlpha = 0.4;
    drawText(
      ctx, "No notes here.",
      x + w / 2, contentStartY + listH / 2 - 8,
      w - 32, "italic 10px sans-serif", C_DIM, "center",
    );
    drawText(
      ctx, "Tap + to write something.",
      x + w / 2, contentStartY + listH / 2 + 8,
      w - 32, "9px sans-serif", C_DIM, "center",
    );
    ctx.globalAlpha = 1;
  }

  // Top list fade
  const fadeH = 8;
  if (s.scrollY > 0) {
    const topFade = ctx.createLinearGradient(x, contentStartY, x, contentStartY + fadeH);
    topFade.addColorStop(0, C_BG);
    topFade.addColorStop(1, "rgba(14,12,10,0)");
    ctx.fillStyle = topFade;
    ctx.fillRect(x, contentStartY, w, fadeH);
  }

  // Bottom list fade
  const btmFade = ctx.createLinearGradient(x, contentStartY + listH - fadeH, x, contentStartY + listH);
  btmFade.addColorStop(0, "rgba(14,12,10,0)");
  btmFade.addColorStop(1, C_BG);
  ctx.fillStyle = btmFade;
  ctx.fillRect(x, contentStartY + listH - fadeH, w, fadeH);

  ctx.restore();

  // ── "New Note" button ─────────────────────────────────────────────────
  const newBtnAreaY = y + h - NEW_BTN_H - PAD;
  _newBtnY = newBtnAreaY;

  ctx.fillStyle = C_BG;
  ctx.fillRect(x, newBtnAreaY - PAD, w, NEW_BTN_H + PAD * 2);

  drawNewButton(ctx, x, newBtnAreaY, w);
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

function drawQuillIcon(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
): void {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const s = size * 0.38;

  // The shaft of the quill — diagonal from bottom-left to top-right
  const shaftX1 = cx - s * 0.7;
  const shaftY1 = cy + s * 0.9;
  const shaftX2 = cx + s * 0.6;
  const shaftY2 = cy - s * 0.9;

  // Feather shape — bezier leaf around the shaft's midpoint
  const midX = (shaftX1 + shaftX2) / 2 - s * 0.05;
  const midY = (shaftY1 + shaftY2) / 2 + s * 0.05;

  // Left edge of feather (curving outward to the left)
  ctx.beginPath();
  ctx.moveTo(shaftX2, shaftY2);                       // tip
  ctx.bezierCurveTo(
    cx - s * 0.15, cy - s * 0.6,                     // cp1
    cx - s * 0.75, cy + s * 0.1,                     // cp2
    shaftX1, shaftY1,                                 // base
  );
  // Right edge back to tip (slightly tighter)
  ctx.bezierCurveTo(
    midX + s * 0.5, midY + s * 0.25,                 // cp1
    cx + s * 0.5,   cy - s * 0.45,                   // cp2
    shaftX2, shaftY2,                                 // tip
  );
  ctx.closePath();
  ctx.fillStyle = "#f0e4d0";
  ctx.globalAlpha = 0.95;
  ctx.fill();

  // Vein lines across the feather
  ctx.globalAlpha = 0.45;
  ctx.strokeStyle = "#c8a878";
  ctx.lineWidth = 0.6;
  for (let i = 1; i <= 4; i++) {
    const t = i / 5;
    const vx = shaftX1 + (shaftX2 - shaftX1) * t;
    const vy = shaftY1 + (shaftY2 - shaftY1) * t;
    ctx.beginPath();
    ctx.moveTo(vx, vy);
    ctx.lineTo(vx - s * 0.35 * (1 - t), vy + s * 0.15 * (1 - t));
    ctx.stroke();
  }

  // Central quill spine
  ctx.globalAlpha = 0.8;
  ctx.strokeStyle = "#c8a878";
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(shaftX1, shaftY1);
  ctx.lineTo(shaftX2, shaftY2);
  ctx.stroke();

  // Ink nib — dark tip
  ctx.globalAlpha = 1.0;
  ctx.fillStyle = "#4a3a2a";
  ctx.beginPath();
  ctx.arc(shaftX2 + 1, shaftY2 - 1, 1.5, 0, Math.PI * 2);
  ctx.fill();

  // Ink dot at writing tip
  ctx.fillStyle = "#3a2a1a";
  ctx.beginPath();
  ctx.arc(shaftX2 + 2, shaftY2 - 2, 0.9, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// ─── The App Object ───────────────────────────────────────────────────────────

const scribeApp: DeviceApp = {
  id: APP_ID,
  name: APP_NAME,
  iconGradient: ["#3d2e1a", "#241a0e"],

  // ── Icon ──────────────────────────────────────────────────────────────────

  drawIcon(ctx, cx, cy, size) {
    drawQuillIcon(ctx, cx, cy, size);
  },

  // ── Content ───────────────────────────────────────────────────────────────

  drawContent(ctx, rect, state, dt) {
    const s = getState(state);

    // Background
    ctx.fillStyle = C_BG;
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

    if (s.activeNoteId !== null) {
      drawDetailView(ctx, rect, s, dt);
    } else {
      drawListView(ctx, rect, s, dt);
    }
  },

  // ── Tap ───────────────────────────────────────────────────────────────────

  handleTap(localX, localY, state) {
    const s = getState(state);
    const rect = state.player.device.appContentRect;
    if (!rect) return false;

    if (s.activeNoteId !== null) {
      // ── Detail view tap handling ──────────────────────────────────────
      const note = s.notes.find(n => n.id === s.activeNoteId);
      if (!note) return false;

      const headerH = 52;
      const device = state.player.device;

      // Category dots in header (y < headerH)
      if (localY < headerH && localY > 3) {
        const catKeys = Object.keys(C_CAT) as NoteCategory[];
        for (let i = 0; i < catKeys.length; i++) {
          const dotCX = rect.x + PAD + i * 20 + 5;
          const dotCY = rect.y + 11;
          const dx = localX + rect.x - dotCX;
          const dy = localY + rect.y - dotCY;
          if (Math.sqrt(dx * dx + dy * dy) < 10) {
            note.category = catKeys[i];
            note.updatedAt = Date.now();
            return true;
          }
        }
      }

      // Pin toggle (top-right of header)
      const pinCX = rect.x + rect.w - PAD - 6;
      const pinCY = rect.y + 10;
      const pdx = localX + rect.x - pinCX;
      const pdy = localY + rect.y - pinCY;
      if (Math.sqrt(pdx * pdx + pdy * pdy) < 12) {
        note.pinned = !note.pinned;
        return true;
      }

      // ── Tap on title area (y 22-42) → edit title ─────────────────────
      if (localY >= 22 && localY < headerH - 4) {
        s.editingField = "title";
        device.editPlaceholder = "Note title...";
        device.editPrefill = note.title;
        device.editCallback = (text: string) => {
          note.title = text || "Untitled";
          note.updatedAt = Date.now();
          s.editingField = null;
        };
        state.chat.inputOpen = true;
        return true;
      }

      // ── Tap on body area → edit body ─────────────────────────────────
      if (localY >= headerH) {
        s.editingField = "body";
        device.editPlaceholder = "Write here... (replaces note body)";
        device.editPrefill = note.body;
        device.editCallback = (text: string) => {
          if (text) {
            note.body = text;
            note.updatedAt = Date.now();
          }
          s.editingField = null;
        };
        state.chat.inputOpen = true;
        return true;
      }

      return false;
    }

    // ── List view tap handling ─────────────────────────────────────────────
    const device = state.player.device;

    // Convert localX/localY to "local" space relative to rect origin
    const absX = localX + rect.x;
    const absY = localY + rect.y;

    // Search bar tap → open search input
    if (localY >= PAD && localY < PAD + SEARCH_H) {
      s.editingField = "search";
      device.editPlaceholder = "Search notes...";
      device.editPrefill = s.searchQuery || "";
      device.editCallback = (text: string) => {
        s.searchQuery = text;
        s.scrollY = 0;
        s.scrollVelocity = 0;
        s.editingField = null;
      };
      state.chat.inputOpen = true;
      return true;
    }

    // Filter row
    const filterRowAbsY = _filterRowY;
    if (absY >= filterRowAbsY && absY <= filterRowAbsY + FILTER_H) {
      for (const hit of _filterHitRects) {
        if (absX >= hit.x && absX <= hit.x + hit.w) {
          s.activeCategory = hit.id;
          s.scrollY = 0;
          s.scrollVelocity = 0;
          return true;
        }
      }
    }

    // New Note button
    if (absY >= _newBtnY && absY <= _newBtnY + NEW_BTN_H) {
      const newNote: ScribeNote = {
        id: makeId(),
        title: "Untitled",
        body: "",
        category: "general",
        createdAt: Date.now(),
        updatedAt: Date.now(),
        pinned: false,
        sharedWith: [],
      };
      s.notes.unshift(newNote);
      s.activeNoteId = newNote.id;
      s.scrollY = 0;
      s.scrollVelocity = 0;
      return true;
    }

    // Note cards
    for (const hit of _cardHitRects) {
      if (absY >= hit.y && absY <= hit.y + CARD_H) {
        s.activeNoteId = hit.id;
        s.scrollY = 0;
        s.scrollVelocity = 0;
        return true;
      }
    }

    return false;
  },

  // ── Drag scroll ───────────────────────────────────────────────────────────

  handleDrag(dy, state) {
    const s = getState(state);
    s.scrollY = Math.max(0, Math.min(s.scrollY + dy, s.maxScrollY));
    s.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const s = getState(state);
    s.scrollVelocity = velocityY;
  },

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  onBack(state) {
    const s = getState(state);
    // Cancel any active edit first
    if (s.editingField) {
      s.editingField = null;
      state.player.device.editCallback = null;
      state.player.device.editPlaceholder = "";
      return true;
    }
    if (s.activeNoteId !== null) {
      // Go back from detail view to note list
      s.activeNoteId = null;
      s.scrollY = 0;
      s.scrollVelocity = 0;
      return true;
    }
    return false; // let overlay navigate to home screen
  },

  onOpen(state) {
    const s = getState(state);
    s.scrollVelocity = 0;
    s.activeNoteId = null;
    s.editingField = null;
  },

  onClose(state) {
    const s = getState(state);
    s.editingField = null;
    state.player.device.editCallback = null;
    state.player.device.editPlaceholder = "";
  },

  // ── Badge: pinned notes with recent updates ────────────────────────────────

  badge(state) {
    // Show the number of pinned notes as a gentle reminder
    const s = getState(state);
    return s.notes.filter(n => n.pinned).length;
  },
};

// ─── Self-Register ────────────────────────────────────────────────────────────

registerApp(scribeApp);
